import { isString } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import type { ValueOf, CategoryWebLink } from '../types';
import {
    CmsContentTypes,
    CmsWebDesignTypes,
    ContentfulTypes,
    DEFAULT_QUERY_PARAMS,
    FILTER_SEPARATORS,
    UiMenuTypes,
    UiTypes,
} from './constants';
import type { ReportData, TransformedCmsConfigItem, TransformedCmsConfigItemLevels } from './types';

export function recursiveFormat(
    json: TransformedCmsConfigItemLevels,
    item: CategoryWebLink,
    parentId: string,
    level: number
) {
    if (item.children && item.children.length > 0) {
        item.children.forEach((child) => recursiveFormat(json, child, item.id, level + 1));
    }
    addItem(json, item, parentId, level);
}

export function addItem(json: TransformedCmsConfigItemLevels, raw: CategoryWebLink, parentId: string, level: number) {
    const item = formatItem(raw);
    if (!item) {
        return;
    }

    addToJson(json, level, parentId, item);
}

export function formatItem(raw: CategoryWebLink): TransformedCmsConfigItem | undefined {
    const linkParams = linkParamsFu(raw);
    if (!linkParams) {
        return;
    }

    return {
        id: raw.id,
        alias: raw.alias,
        ...linkParams,
        reportData: reportData(raw),
        ...displayData(raw),
        ...menuType(raw),
        ...badge(raw),
        ...webIcon(raw),
        ...designStyle(raw),
    };
}

export function reportData({ alias, webLink }: CategoryWebLink): ReportData {
    const {
        category_id: categoryId,
        sub_category_id: subCategoryId,
        nested_subcategory_id: nestedSubCategoryId,
    } = webLink ?? {};

    return {
        alias,
        ...(categoryId && { categoryId }),
        ...(subCategoryId && { subCategoryId }),
        ...(nestedSubCategoryId && { nestedSubCategoryId }),
    };
}

export function addToJson(
    json: TransformedCmsConfigItemLevels,
    level: number,
    parentId: string,
    item: TransformedCmsConfigItem
) {
    json[`level_${level}`] = json[`level_${level}`] || {};
    json[`level_${level}`][parentId] = json[`level_${level}`][parentId] || [];
    json[`level_${level}`][parentId].push(item);
}

export function badge(item: CategoryWebLink): {
    badge?: string;
} {
    return item.badge ? { badge: item.badge.toLowerCase() } : {};
}

const isIconUrl = (value: string) => /^(https?:)?\/\//.test(value);

export function webIcon(item: CategoryWebLink): {
    webIcon?: string;
} {
    return item.webIcon && isIconUrl(item.webIcon) ? { webIcon: item.webIcon } : {};
}

export function designStyle(item: CategoryWebLink): {
    designStyle?: string;
} {
    return item.webDesignStyle ? { designStyle: item.webDesignStyle } : {};
}

export function menuType(item: CategoryWebLink): {
    menuType?: ValueOf<typeof UiMenuTypes>;
} {
    const webDesignStyle = item.webDesignStyle;
    if (!webDesignStyle) {
        return {};
    }

    let menuType: ValueOf<typeof UiMenuTypes> | undefined;
    switch (webDesignStyle) {
        case CmsWebDesignTypes.PANEL:
            menuType = UiMenuTypes.MULTI_PANEL;
            break;
        case CmsWebDesignTypes.LIST:
            menuType = UiMenuTypes.DEFAULT;
            break;
        case CmsWebDesignTypes.BUCKETS:
            menuType = UiMenuTypes.BUCKET;
            break;
        case CmsWebDesignTypes.BUCKETS_WITH_TITLE:
            menuType = UiMenuTypes.BUCKETS_WITH_TITLE;
            break;
        case CmsWebDesignTypes.BUCKETS_WITHOUT_TITLE:
            menuType = UiMenuTypes.BUCKETS_WITHOUT_TITLE;
            break;
        default:
            break;
    }

    return menuType ? { menuType } : {};
}

export function displayData(item: CategoryWebLink): {
    displayData?: {
        title: string;
        subTitle: string | null;
    };
} {
    const title = item.alias;
    let subTitle: string | undefined | null = item.subtitle;
    if (!title) {
        return {};
    }
    if (!subTitle) {
        subTitle = null;
    }

    return { displayData: { title, subTitle } };
}

export function webLinkTypeDecider(webLink: CategoryWebLink['webLink']): {
    type: ValueOf<typeof UiTypes>;
    urlParams?: {
        id?: string;
        categoryId?: string;
        subCategoryId?: string;
        nestedSubCategoryId?: string;
        filterUid?: string;
        mobileFilter?: string;
        query?: string;
        queryParams?: string;
        tag?: string;
        tagType?: string;
    };
} | null {
    switch (webLink?.type) {
        case ContentfulTypes.CATEGORY:
            return categoryTransformer(webLink);
        case ContentfulTypes.SUB_CATEGORY:
            return subCategoryTransformer(webLink);
        case ContentfulTypes.NSC:
            return nscTransformer(webLink);
        case ContentfulTypes.SEARCH_QUERY:
            return searchQueryTransformer(webLink);
        case ContentfulTypes.FILTERED_LISTING:
            return filteredListingPageTransformer(webLink);
        case ContentfulTypes.LOGO_MAKER_IDEAS:
            return logoMakerTransformer(webLink);
        default:
            return staticTransformer();
    }
}

export function linkParamsFu(item: CategoryWebLink): {
    type: ValueOf<typeof UiTypes>;
    urlParams?: {
        queryParams?: string;
        url?: string;
        slug?: string;
        id?: string;
        entryId?: string;
        categoryId?: string;
        subCategoryId?: string;
        nestedSubCategoryId?: string;
        filterUid?: string;
        mobileFilter?: string;
        query?: string;
        tag?: string;
        tagType?: string;
    };
} | null {
    const webLink = item.webLink;
    const cmsContentType = webLink?.cmsContentType;

    if (webLink?.contentType === ContentfulTypes.LINK_URL) {
        return urlLinkTransformer(webLink);
    } else if (cmsContentType) {
        switch (cmsContentType) {
            case CmsContentTypes.VERTICAL_EXPERIENCE_PAGE:
                return cmsLinkTransformer(webLink, UiTypes.STORE);
            case CmsContentTypes.LANDING_PAGE:
                return cmsLinkTransformer(webLink, UiTypes.LANDING_PAGE);
            case CmsContentTypes.CONTENT_PAGE:
                return cmsLinkTransformer(webLink, UiTypes.CONTENT_PAGE);
            default:
                return null;
        }
    } else {
        return webLinkTypeDecider(webLink);
    }
}

export function filters(data: NonNullable<CategoryWebLink['webLink']>): { queryParams?: string } {
    return data.encoded_filters ? { queryParams: data.encoded_filters } : {};
}

export function categoryTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const id = data.category_id;
    return id ? { type: UiTypes.CATEGORY, urlParams: { id, ...filters(data) } } : null;
}

export function subCategoryTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const categoryId = data.category_id;
    const id = data.sub_category_id;
    return id && categoryId ? { type: UiTypes.SUB_CATEGORY, urlParams: { categoryId, id, ...filters(data) } } : null;
}

export function nscTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const id = data.nested_subcategory_id;
    const categoryId = data.category_id;
    const subCategoryId = data.sub_category_id;
    return id && categoryId && subCategoryId
        ? { type: UiTypes.NSC, urlParams: { categoryId, subCategoryId, id, ...filters(data) } }
        : null;
}

export function searchQueryTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const searchTerm = data.search_term;
    const categoryId = data.category_id;
    const subCategoryId = data.sub_category_id;
    const additionalParams = searchTerm && categoryId && subCategoryId ? { categoryId, id: subCategoryId } : undefined;
    return searchTerm
        ? { type: UiTypes.SEARCH_QUERY, urlParams: { query: searchTerm, ...additionalParams, ...filters(data) } }
        : null;
}

export function cmsLinkTransformer(data: NonNullable<CategoryWebLink['webLink']>, type: ValueOf<typeof UiTypes>) {
    const slug = data.slug;
    const entryId = data.cmsEntryId;
    return slug && entryId ? { type, urlParams: { slug, entryId } } : null;
}

export function urlLinkTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const url = data?.url;
    return url ? { type: UiTypes.LINK_URL, urlParams: { url, ...filters(data) } } : null;
}

export function filteredListingPageTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const categoryId = data.category_id;
    const subCategoryId = data.sub_category_id;
    const nestedSubcategoryId = data.nested_subcategory_id;
    const filterUid = data.leadingFilter;
    const encodedFilters = data.encoded_filters;
    const additionalParams = nestedSubcategoryId ? { nestedSubCategoryId: nestedSubcategoryId } : undefined;
    return categoryId && subCategoryId && filterUid && encodedFilters
        ? {
              type: UiTypes.FILTERED_SC,
              urlParams: {
                  categoryId,
                  id: subCategoryId,
                  filterUid,
                  mobileFilter: encodedFilters,
                  ...additionalParams,
                  ...filters(data),
              },
          }
        : null;
}

export function logoMakerTransformer(data: NonNullable<CategoryWebLink['webLink']>) {
    const tag = data.tag;
    const tagType = data.tagType;
    return tag ? { type: UiTypes.LOGO_MAKER_IDEAS, urlParams: { tag, tagType, ...filters(data) } } : null;
}

export function staticTransformer() {
    return { type: UiTypes.STATIC };
}

export const filtersToQueryString = (filters) =>
    Object.entries(filters || {})
        .map(
            ([filterName, filterValue]) =>
                `${filterName}${FILTER_SEPARATORS.PARAM_VALUE}${_stringifyActiveFilterValue(filterValue)}`
        )
        .join(FILTER_SEPARATORS.PARAMS);

export const formatQueryParams = (
    queryConfig?: TransformedCmsConfigItem['urlParams']
): {
    query: {
        source: string;
        ref?: string;
    };
} => {
    const { filter, queryParams = {}, source } = queryConfig || {};
    const defaultQueryParams = { ...DEFAULT_QUERY_PARAMS, ...(source && { source }) };

    // temporary support in both data structures
    if (isString(queryParams)) {
        return {
            query: {
                ...defaultQueryParams,
                ref: queryParams,
            },
        };
    }

    if (!filter) {
        return { query: { ...defaultQueryParams, ...queryParams } };
    }

    return {
        query: {
            ...defaultQueryParams,
            ...queryParams,
            ref: filtersToQueryString(filter),
        },
    };
};

const _stringifyActiveFilterValue = (value) =>
    Array.isArray(value) ? value.join(FILTER_SEPARATORS.ARRAY_VALUE) : value;

export const getSlug = (key: string) => translate(key);
export const getCategorySlug = (id: string) => getSlug(`categories.category_${id}.cached_slug`);
export const getSubCategorySlug = (id: string) => getSlug(`sub_categories.sub_category_${id}.cached_slug`);
export const getNestedSubCategorySlug = (id: string, subCategoryId: string) =>
    getSlug(`sub_categories.sub_category_${subCategoryId}.nested_sub_cat_${id}.cached_slug`);
